/**
 * Landing Screen Component
 * Welcome page with language switcher and start button
 */

import React from 'react';
import { useTranslation } from '../../../hooks/useTranslation';
import { LanguageSwitcher, Language } from '../../common/LanguageSwitcher';
import styles from './LandingScreen.module.css';

export type BotDifficulty = 'easy' | 'medium' | 'hard';

interface LandingScreenProps {
  currentLang: Language;
  onLanguageChange: (lang: Language) => void;
  onStartGame: () => void;
  onStartDemo: () => void;
  botDifficulty: BotDifficulty;
  onDifficultyChange: (difficulty: BotDifficulty) => void;
}

export const LandingScreen: React.FC<LandingScreenProps> = ({
  currentLang,
  onLanguageChange,
  onStartGame,
  onStartDemo,
  botDifficulty,
  onDifficultyChange,
}) => {
  const { t } = useTranslation();

  return (
    <section
      id="screen-landing"
      className={`screen ${styles.screen} active`}
      role="main"
      aria-label={t('landing.aria.screen')}
    >
      <div className={styles.logoBadge} role="img" aria-label={t('landing.aria.logo')}>
        🃏
      </div>

      <h1 className={styles.gameTitle}>{t('landing.title')}</h1>

      <p className={styles.gameSubtitle}>{t('landing.subtitle')}</p>

      {/* Era illustration strip: each emoji marks a historical period the
          game's question corpus covers. Purely decorative — aria-hidden. */}
      <div className={styles.eraStrip} aria-hidden="true" title="Eras: Kievan Rus → Modern">
        <span>🏰</span>
        <span>⚔️</span>
        <span>👑</span>
        <span>🦅</span>
        <span>📜</span>
        <span>🕊️</span>
        <span>⭐</span>
        <span>🌟</span>
      </div>

      <LanguageSwitcher
        currentLang={currentLang}
        onLanguageChange={onLanguageChange}
      />

      {/* Bot Difficulty Selector */}
      <div className={styles.difficultySection} aria-label={t('landing.aria.difficultySection')}>
        <div className={styles.difficultyLabel}>{t('landing.difficulty.label')}</div>
        <div className={styles.difficultyButtons} role="radiogroup" aria-label={t('landing.aria.difficultyGroup')}>
          {(['easy', 'medium', 'hard'] as BotDifficulty[]).map((diff) => {
            const levelLabel = t(`landing.difficulty.${diff}`);
            return (
              <button
                key={diff}
                className={`${styles.difficultyBtn} ${botDifficulty === diff ? styles.active : ''}`}
                onClick={() => onDifficultyChange(diff)}
                role="radio"
                aria-checked={botDifficulty === diff}
                aria-label={t('landing.aria.difficultyOption', { level: levelLabel })}
              >
                {levelLabel}
              </button>
            );
          })}
        </div>
      </div>

      <div className={styles.startButtons}>
        <button
          className={styles.btnStart}
          onClick={onStartGame}
          aria-label={t('landing.aria.startGame')}
        >
          {t('landing.startGame')}
        </button>
        <button
          className={styles.btnDemo}
          onClick={onStartDemo}
          aria-label={t('landing.aria.juryDemo')}
          title={t('landing.juryDemoTooltip')}
        >
          {t('landing.juryDemo')}
        </button>
      </div>

      <div className={styles.featurePills} aria-label={t('landing.aria.features')}>
        <span className={styles.pill}>{t('landing.features.durak')}</span>
        <span className={styles.pill}>{t('landing.features.retrieval')}</span>
        <span className={styles.pill}>{t('landing.features.srs')}</span>
        <span className={styles.pill}>{t('landing.features.perspectives')}</span>
        <span className={styles.pill}>{t('landing.features.accessibility')}</span>
        <span className={styles.pill}>{t('landing.features.languages')}</span>
      </div>

      <div className={styles.stats}>
        <div className={styles.statBox}>
          <div className={styles.statValue}>36</div>
          <div className={styles.statLabel}>{t('landing.stats.cards')}</div>
        </div>
        <div className={styles.statBox}>
          <div className={styles.statValue}>200</div>
          <div className={styles.statLabel}>{t('landing.stats.questions')}</div>
        </div>
        <div className={styles.statBox}>
          <div className={styles.statValue}>3</div>
          <div className={styles.statLabel}>{t('landing.stats.difficultyTiers')}</div>
        </div>
        <div className={styles.statBox}>
          <div className={styles.statValue}>25</div>
          <div className={styles.statLabel}>{t('landing.stats.perspectives')}</div>
        </div>
      </div>
    </section>
  );
};

export default LandingScreen;
